import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const db = supabase as any;

export type Field = {
  key: string;
  label: string;
  type?: "text" | "textarea" | "date" | "number" | "select";
  options?: string[];
  required?: boolean;
  inList?: boolean;
};

type Props = {
  table: string;
  orgId: string;
  title: string;
  fields: Field[];
  canEdit: boolean;
  canDelete: boolean;
  orderBy?: string;
  filter?: Record<string, string>;
  defaults?: Record<string, unknown>;
  render?: (row: Record<string, any>) => React.ReactNode;
  onChange?: () => void;
};

export const EntityManager = ({ table, orgId, title, fields, canEdit, canDelete, orderBy = "created_at", filter, defaults, render, onChange }: Props) => {
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [editing, setEditing] = useState<Record<string, any> | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    let q = db.from(table).select("*").eq("org_id", orgId);
    Object.entries(filter ?? {}).forEach(([k, v]) => (q = q.eq(k, v)));
    const { data, error } = await q.order(orderBy, { ascending: true });
    if (error) toast.error(error.message);
    else setRows(data ?? []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [table, orgId, JSON.stringify(filter)]);

  const save = async () => {
    if (!editing) return;
    for (const f of fields) if (f.required && !editing[f.key]) return toast.error(`${f.label} is required`);
    setSaving(true);
    const payload: Record<string, any> = { ...defaults, ...filter, org_id: orgId };
    fields.forEach((f) => {
      const v = editing[f.key] === "" ? null : editing[f.key] ?? null;
      // New records: leave blanks out so the database applies its defaults.
      // Edits: don't blank dropdowns/scores (many are required) — keep the saved value.
      if (v === null && (!editing.id || f.type === "select" || f.type === "number")) return;
      payload[f.key] = v;
    });
    const res = editing.id
      ? await db.from(table).update(payload).eq("id", editing.id)
      : await db.from(table).insert(payload);
    setSaving(false);
    if (res.error) return toast.error(res.error.message);
    toast.success("Saved");
    setEditing(null);
    load();
    onChange?.();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this record? This is logged.")) return;
    const { error } = await db.from(table).delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
    onChange?.();
  };

  const listFields = fields.filter((f) => f.inList);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-sans font-semibold text-lg">{title}</h3>
        {canEdit && (
          <Button size="sm" variant="accent" onClick={() => setEditing({})}><Plus className="h-4 w-4 mr-1" />Add</Button>
        )}
      </div>
      {rows.length === 0 && <p className="text-sm text-muted-foreground">Nothing recorded yet.</p>}
      <div className="grid gap-2">
        {rows.map((r) => (
          <div key={r.id} className="border border-border rounded-md p-3 flex gap-3 items-start justify-between bg-card">
            <div className="text-sm space-y-1 min-w-0">
              {render ? render(r) : listFields.map((f) => (
                <div key={f.key}><span className="text-muted-foreground">{f.label}: </span>{r[f.key] ?? "—"}</div>
              ))}
            </div>
            <div className="flex gap-1 shrink-0">
              {canEdit && <Button size="icon" variant="ghost" aria-label="Edit" onClick={() => setEditing(r)}><Pencil className="h-4 w-4" /></Button>}
              {canDelete && <Button size="icon" variant="ghost" aria-label="Delete" onClick={() => remove(r.id)}><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        ))}
      </div>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-sans">{editing?.id ? "Edit" : "Add"} — {title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <FieldInput key={f.key} field={f} value={editing?.[f.key]} onChange={(v) => setEditing((e) => ({ ...e, [f.key]: v }))} />
            ))}
          </div>
          <DialogFooter><Button variant="accent" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const FieldInput = ({ field: f, value, onChange }: { field: Field; value: any; onChange: (v: any) => void }) => (
  <div className="space-y-1">
    <Label className="font-sans">{f.label}{f.required ? " *" : ""}</Label>
    {f.type === "textarea" ? (
      <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    ) : f.type === "select" ? (
      <select className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {f.options?.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : (
      <Input type={f.type ?? "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />
    )}
  </div>
);
